import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ClipboardList, 
  AlertTriangle, 
  Scale, 
  Users, 
  Calendar, 
  CheckCircle, 
  XCircle,
  Eye,
  Edit,
  Clock,
  Trash2,
  Search,
  Plus,
  TrendingUp,
  FileText,
  MapPin,
  Image,
  X
} from 'lucide-react';

function TradersInspection() {
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [traderdata, setTraderdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Photo modal states
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState({ url: '', title: '' });

  // Setup axios instance
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    headers: { 'Accept': 'application/json' }
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (!token) {
      navigate('/');
    }
    return config;
  });

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/storage')) return `http://localhost:8000${path}`;
    return `http://localhost:8000/storage/${path}`;
  };

  const openPhotoModal = (imageUrl, title) => {
    if (!imageUrl) return;
    setCurrentPhoto({ url: imageUrl, title });
    setIsPhotoModalOpen(true);
  };

  // Format date properly
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Fetch traders inspection data
  const FetchTradersdata = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tradersinspection');
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        setTraderdata(response.data.data || []);
      } else {
        setError('Failed to fetch data');
        setTraderdata([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Error loading inspection data');
      setTraderdata([]);
    } finally {
      setLoading(false);
    }
  };

  // Update inspection data
  const updateInspection = async (updatedData) => {
    try {
      const response = await api.put(`/tradersinspection/${updatedData.id}`, updatedData);
      if (response.data.success) {
        await FetchTradersdata();
        setIsEditModalOpen(false);
      }
    } catch (error) {
      console.error('Error updating inspection:', error);
      setError('Failed to update inspection');
    }
  };

  // Delete inspection


  useEffect(() => {
    FetchTradersdata();
  }, []);

  // ────────── STATISTICS ──────────
  const stats = useMemo(() => {
    if (!traderdata || !Array.isArray(traderdata) || traderdata.length === 0) {
      return { 
        total: 0, 
        withViolations: 0, 
        penalties: 0, 
        uniqueTraders: 0,
        pendingReview: 0,
        completed: 0,
        thisMonth: 0,
        todayInspections: 0,
        recent: 0
      };
    }
    
    const total = traderdata.length;
    const withViolations = traderdata.filter(i => i.violation_type && i.violation_type !== 'No Violation').length;
    const penalties = traderdata.filter(i => i.action_taken === 'action_taken').length;
    const uniqueTraders = new Set(traderdata.map(i => i.trader_name).filter(Boolean)).size;
    const pendingReview = traderdata.filter(i => i.status === 'Pending Review').length;
    const completed = traderdata.filter(i => i.status === 'Completed').length;
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonth = traderdata.filter(i => {
      if (!i.created_at) return false;
      const inspectionDate = new Date(i.created_at);
      return inspectionDate.getMonth() === currentMonth && 
             inspectionDate.getFullYear() === currentYear;
    }).length;
    
    const today = new Date().toISOString().split('T')[0];
    const todayInspections = traderdata.filter(i => {
      if (!i.created_at) return false;
      return i.created_at.split('T')[0] === today;
    }).length;
    
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recent = traderdata.filter(i => {
      if (!i.created_at) return false;
      return new Date(i.created_at) >= sevenDaysAgo;
    }).length;
    
    return { 
      total, 
      withViolations, 
      penalties, 
      uniqueTraders,
      pendingReview,
      completed,
      thisMonth,
      todayInspections,
      recent
    };
  }, [traderdata]);

  // Filter inspections
  const filteredInspections = Array.isArray(traderdata) ? traderdata.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    
    const traderMatch = item.trader_name ? String(item.trader_name).toLowerCase().includes(searchLower) : false;
    const commodityMatch = item.commodity_checked ? String(item.commodity_checked).toLowerCase().includes(searchLower) : false;
    const violationMatch = item.violation_type ? String(item.violation_type).toLowerCase().includes(searchLower) : false;
    const idMatch = item.id ? String(item.id).toLowerCase().includes(searchLower) : false;
    const phoneMatch = item.phone ? String(item.phone).toLowerCase().includes(searchLower) : false;
    
    return traderMatch || commodityMatch || violationMatch || idMatch || phoneMatch;
  }) : [];

  const openViewModal = (inspection) => {
    setSelectedInspection(inspection);
    setIsViewModalOpen(true);
  };

  const openEditModal = (inspection) => {
    setSelectedInspection(inspection);
    setIsEditModalOpen(true);
  };



  const handleUpdate = (updatedData) => {
    updateInspection(updatedData);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Pending Review':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getViolationColor = (violation) => {
    return violation === 'No Violation' || !violation 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading inspection data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button 
            onClick={FetchTradersdata}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <ClipboardList className="h-10 w-10 text-indigo-600" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Inspection Reports
                  </h1>
                  <p className="text-gray-600 mt-2">View and manage all inspection records</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Inspections</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
              </div>
              <div className="bg-indigo-100 rounded-full p-3">
                <ClipboardList className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Violations Found</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.withViolations}</p>
                <p className="text-xs text-red-600 mt-1">{((stats.withViolations / stats.total) * 100 || 0).toFixed(1)}% of total</p>
              </div>
              <div className="bg-red-100 rounded-full p-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Penalties Issued</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.penalties}</p>
              </div>
              <div className="bg-yellow-100 rounded-full p-3">
                <Scale className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Pending Review</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.pendingReview}</p>
              </div>
              <div className="bg-orange-100 rounded-full p-3">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by ID, trader, commodity, violation or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-indigo-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-indigo-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">S.No</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Inspection Date</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">Inspector</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Trader</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Phone</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden lg:table-cell">Commodity</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Violation</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">Action</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Photo Evidence</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">License</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInspections.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center text-gray-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No inspections found</p>
                    </td>
                  </tr>
                ) : (
                  filteredInspections.map((item, index) => (
                    <tr key={item.id} className="hover:bg-indigo-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 text-sm">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm">{formatDate(item.created_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm hidden md:table-cell">{item.inspector_name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 text-sm font-medium">{item.trader_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 text-sm hidden sm:table-cell">{item.phone || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm hidden lg:table-cell">{item.commodity_checked}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getViolationColor(item.violation_type)}`}>
                          {item.violation_type || 'No Violation'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm hidden md:table-cell">{item.action_taken || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {item.photo_evidence && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(item.photo_evidence), 'Evidence Photo')}
                              className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                              title="View Evidence Photo"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                          )}
                          {!item.photo_evidence && (
                            <span className="text-gray-400 text-xs">No photo</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {item.photo_license && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(item.photo_license), 'License Photo')}
                              className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                              title="View License Photo"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                          )}
                          {!item.photo_license && (
                            <span className="text-gray-400 text-xs">No photo</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openViewModal(item)}
                            className="p-1 text-indigo-600 hover:text-indigo-800 transition rounded-lg hover:bg-indigo-100"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        
                       
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* View Detail Modal */}
        {isViewModalOpen && selectedInspection && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Inspection Details
                  </h2>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Inspection ID</p>
                    <p className="text-base font-medium text-gray-900">{selectedInspection.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="text-base font-medium">{formatDate(selectedInspection.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Inspector</p>
                    <p className="text-base font-medium">{selectedInspection.inspector_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedInspection.status)}`}>
                      {selectedInspection.status || 'Pending Review'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Trader Name</p>
                    <p className="text-base font-medium">{selectedInspection.trader_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-base font-medium">{selectedInspection.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Commodity</p>
                    <p className="text-base font-medium">{selectedInspection.commodity_checked}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Violation Type</p>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getViolationColor(selectedInspection.violation_type)}`}>
                      {selectedInspection.violation_type || 'No Violation'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Action Taken</p>
                    <p className="text-base font-medium">{selectedInspection.action_taken || 'No Action'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="text-base font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      {selectedInspection.location || 'N/A'}
                    </p>
                  </div>
                  
                  {/* Photos in View Modal */}
                  {(selectedInspection.photo_evidence || selectedInspection.photo_license) && (
                    <div className="sm:col-span-2 mt-4">
                      <p className="text-sm text-gray-500 mb-3">Photos</p>
                      <div className="flex gap-4">
                        {selectedInspection.photo_evidence && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Evidence Photo</p>
                            <button
                              onClick={() => openPhotoModal(getImageUrl(selectedInspection.photo_evidence), 'Evidence Photo')}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                            >
                              <Image className="w-5 h-5" />
                            </button>
                          </div>
                        )}
                        {selectedInspection.photo_license && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">License Photo</p>
                            <button
                              onClick={() => openPhotoModal(getImageUrl(selectedInspection.photo_license), 'License Photo')}
                              className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                            >
                              <Image className="w-5 h-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Modal */}
        {isPhotoModalOpen && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl w-full">
              <button
                onClick={() => setIsPhotoModalOpen(false)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition"
              >
                <X className="w-8 h-8" />
              </button>
              <div className="bg-white rounded-2xl overflow-hidden">
                <div className="p-4 bg-gray-100 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">{currentPhoto.title}</h3>
                </div>
                <div className="p-4 flex justify-center items-center min-h-[400px] bg-gray-900">
                  <img
                    src={currentPhoto.url}
                    alt={currentPhoto.title}
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                      e.target.alt = 'Image not found';
                    }}
                  />
                </div>
                <div className="p-4 bg-gray-100 text-center">
                  <button
                    onClick={() => setIsPhotoModalOpen(false)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && selectedInspection && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-6">Edit Inspection Report</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Violation Type</label>
                    <input
                      type="text"
                      defaultValue={selectedInspection.violation_type || ''}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      onChange={(e) => setSelectedInspection({...selectedInspection, violation_type: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Action Taken</label>
                    <select 
                      defaultValue={selectedInspection.action_taken || 'No Action'} 
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      onChange={(e) => setSelectedInspection({...selectedInspection, action_taken: e.target.value})}
                    >
                      <option>Verbal Warning</option>
                      <option>Written Warning</option>
                      <option>Penalty Issued</option>
                      <option>Product Seized</option>
                      <option>Business Temporarily Closed</option>
                      <option>No Action</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <select 
                      defaultValue={selectedInspection.status || 'Pending Review'} 
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      onChange={(e) => setSelectedInspection({...selectedInspection, status: e.target.value})}
                    >
                      <option>Completed</option>
                      <option>Pending Review</option>
                    </select>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
                    <button
                      onClick={() => setIsEditModalOpen(false)}
                      className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleUpdate(selectedInspection);
                      }}
                      className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TradersInspection;